<template>
  <div v-if="kind && !native" ref="preview" class="todo-file-preview">
    <div v-if="error" class="todo-file-preview__notice" role="status">
      <span>Vorschau nicht verfügbar. Du kannst die Datei herunterladen.</span>
      <v-btn text small color="primary" @click="load">Erneut versuchen</v-btn>
    </div>
    <div v-else-if="!ready" class="todo-file-preview__notice" role="status">
      <v-progress-circular v-if="loading" indeterminate size="20" width="2" color="primary"></v-progress-circular>
      <span>{{ loading ? "Vorschau wird geladen …" : "Dateivorschau" }}</span>
    </div>
    <button v-else-if="kind === 'image'" type="button" class="todo-file-preview__image" :aria-label="attachment.originalName + ' vergrössern'" @click="$emit('open-image')">
      <img :src="url" :alt="attachment.originalName" @error="fail" />
    </button>
    <iframe v-else-if="kind === 'pdf'" :src="url" :title="attachment.originalName" class="todo-file-preview__pdf" @error="fail"></iframe>
    <video v-else-if="kind === 'video'" :src="url" controls playsinline preload="metadata" :aria-label="attachment.originalName" @error="fail"></video>
    <pre v-else-if="kind === 'text'" class="todo-file-preview__text">{{ text }}</pre>
    <span v-if="ready && truncated" class="todo-file-preview__notice">Die Vorschau zeigt die ersten 20'000 Zeichen. Die vollständige Datei kannst du herunterladen.</span>
  </div>
</template>

<script>
import api from "@/api"
import { attachmentPreviewKind, isFlutterApp } from "@/services/todoAttachmentMedia"

export default {
  name: "TodoAttachmentPreview",
  props: { attachment: { type: Object, required: true } },
  data() {
    return { native: isFlutterApp(), url: "", text: "", truncated: false, loading: false, ready: false, error: false, observer: null, request: 0, disposed: false }
  },
  computed: {
    kind() { return attachmentPreviewKind(this.attachment) },
  },
  mounted() {
    window.addEventListener("flutterInAppWebViewPlatformReady", this.onNativeReady)
    if (this.native || !this.kind) return
    if (typeof IntersectionObserver === "undefined") return this.load()
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.observer.disconnect()
        this.load()
      }
    }, { rootMargin: "100px", threshold: 0.01 })
    this.observer.observe(this.$refs.preview)
  },
  beforeDestroy() {
    this.disposed = true
    this.request += 1
    if (this.observer) this.observer.disconnect()
    window.removeEventListener("flutterInAppWebViewPlatformReady", this.onNativeReady)
    this.release()
  },
  methods: {
    release() {
      if (this.url.startsWith("blob:")) URL.revokeObjectURL(this.url)
      this.url = ""
    },
    onNativeReady() {
      this.native = isFlutterApp()
      if (!this.native) return
      this.request += 1
      if (this.observer) this.observer.disconnect()
      this.release()
      this.text = ""
      this.loading = false
      this.ready = false
    },
    fail() { this.error = true },
    async load() {
      if (this.loading || this.disposed || isFlutterApp() || !this.kind) return
      const request = ++this.request
      this.release()
      this.error = false
      this.ready = false
      this.loading = true
      try {
        const att = this.attachment
        const limit = this.kind === "text" ? 1024 * 1024 : 25 * 1024 * 1024
        if (this.kind !== "video" && att.size > limit) throw new Error("Preview limit")
        if (this.kind === "video") {
          const result = await api.toDoList.getAttachmentUrl(att.todoId, att._id, true)
          if (request !== this.request || this.disposed || isFlutterApp()) return
          if (!/^https:\/\//.test(result.url || "")) throw new Error("Invalid preview URL")
          this.url = result.url
        } else {
          let blob
          if (att.provider === "cloudflare_r2") blob = await api.toDoList.previewAttachment(att.todoId, att._id)
          else if (this.kind === "image") blob = await api.toDoList.fetchAttachment(att.filePath, false)
          else blob = (await api.toDoList.downloadAttachment(att.todoId, att._id)).blob
          if (request !== this.request || this.disposed || isFlutterApp()) return
          if (blob.size > limit) throw new Error("Preview limit")
          if (this.kind === "text") {
            const content = await blob.text()
            if (request !== this.request || this.disposed || isFlutterApp()) return
            this.text = content.slice(0, 20000)
            this.truncated = content.length > 20000
          } else {
            // Legacy PDFs can have a generic download MIME type.
            this.url = URL.createObjectURL(this.kind === "pdf" ? new Blob([blob], { type: "application/pdf" }) : blob)
          }
        }
        this.ready = true
      } catch (_) {
        if (request === this.request && !this.disposed) this.error = true
      } finally {
        if (request === this.request && !this.disposed) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.todo-file-preview { width: 32rem; max-width: 100%; min-width: 0; border-radius: 0.625rem; overflow: hidden; background: rgba(0, 0, 0, 0.14); }
.todo-file-preview__notice { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; padding: 0.75rem; font-size: 0.875rem; line-height: 1.5; }
.todo-file-preview__image { display: block; width: 100%; border: 0; padding: 0; background: transparent; cursor: zoom-in; }
.todo-file-preview img { display: block; width: 100%; max-height: 24rem; object-fit: contain; }
.todo-file-preview__pdf { display: block; width: 100%; height: 30rem; border: 0; background: white; }
.todo-file-preview video { display: block; width: 100%; max-height: 26rem; }
.todo-file-preview__text { max-height: 24rem; overflow: auto; margin: 0; padding: 1rem; font-size: 1rem; line-height: 1.5; white-space: pre-wrap; overflow-wrap: anywhere; }
@media (max-width: 600px) { .todo-file-preview__pdf { height: 22rem; } }
</style>
